import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { createInviteToken } from '../../onboarding/utils/inviteToken';
import { buildInviteContent } from '../../onboarding/utils/inviteContent';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';
import { memberConstraintMessage } from '../../../shared/utils/memberConstraintError';
import { fetchMemberRecord, groupByCycle } from '../../../shared/utils/memberRecord';
import { downloadMemberRecordPdf } from '../../../shared/utils/downloadMemberRecordPdf';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:    'bg-success-100 text-success-800',
  defaulted: 'bg-error-100 text-error-800',
  removed:   'bg-neutral-100 text-neutral-500',
  pending:   'bg-warning-100 text-warning-800',
  inactive:  'bg-neutral-100 text-neutral-500',
};

const STATUS_LABELS = {
  active: 'Active', defaulted: 'Defaulted', removed: 'Removed',
  pending: 'Pending', inactive: 'Inactive',
};

const PAYMENT_STATUS_LABELS = {
  pending: 'Pending', member_submitted: 'Awaiting review',
  paid: 'Paid', rejected: 'Rejected', late: 'Late', covered: 'Covered',
};

const PAYMENT_STATUS_STYLES = {
  paid:             'bg-success-100 text-success-800',
  member_submitted: 'bg-warning-100 text-warning-800',
  rejected:         'bg-error-100 text-error-800',
  pending:          'bg-neutral-100 text-neutral-600',
  late:             'bg-warning-100 text-warning-800',
  covered:          'bg-primary-100 text-primary-800',
};

const NOTIFICATION_LABELS = {
  whatsapp_only: 'WhatsApp only',
  whatsapp_sms:  'WhatsApp + SMS',
  sms_only:      'SMS only',
  in_app_only:   'In-app only',
};

// ─── Invite state ─────────────────────────────────────────────────────────────
// DERIVED, not read straight off status: the 'expired' enum value is never
// written by any code (see inviteToken.js) — expiry is computed from expires_at,
// exactly as the onboarding wizard does (InviteOnboardingWizard.jsx:323).
//   pending  → sent, still within the 7-day window, not accepted
//   expired  → window passed, never accepted
//   accepted → member joined + set PIN; the link is spent
//   none     → no invite row (defensive; add-time always creates one)
const inviteState = (invite) => {
  if (!invite) return 'none';
  if (invite.status === 'accepted') return 'accepted';
  if (invite.expires_at && new Date(invite.expires_at) < new Date()) return 'expired';
  return 'pending';
};

const INVITE_LABELS = {
  pending:  { text: 'Invite pending', cls: 'bg-warning-100 text-warning-800' },
  expired:  { text: 'Invite expired', cls: 'bg-error-100 text-error-800' },
  accepted: { text: 'Joined ✓',       cls: 'bg-success-100 text-success-800' },
  none:     { text: 'No invite',      cls: 'bg-neutral-100 text-neutral-600' },
};

// ─── Inline overlay modal wrapper ─────────────────────────────────────────────

const SubModal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
    <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl max-h-[90vh] flex flex-col">
      <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200 flex-shrink-0">
        <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
      </div>
      <div className="overflow-y-auto flex-1">{children}</div>
    </div>
  </div>
);

// ─── Edit Member sub-modal ────────────────────────────────────────────────────

const EditMemberSubModal = ({ member, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name:                    member.name,
    email:                   member.email || '',
    whatsapp_number:         member.whatsapp_number,
    sms_number:              member.sms_number,
    notification_preference: member.notification_preference,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      const { data: updated, error: updateErr } = await supabaseClient
        .from('members')
        .update({
          name:                    form.name.trim(),
          email:                   form.email.trim() || null,
          whatsapp_number:         form.whatsapp_number.trim(),
          sms_number:              form.sms_number.trim(),
          notification_preference: form.notification_preference,
        })
        .eq('id', member.id)
        .select()
        .single();
      if (updateErr) throw updateErr;

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'member_edited',
        actor_id:       user?.id ?? null,
        actor_role:     'admin',
        old_value: {
          name: member.name, email: member.email,
          whatsapp_number: member.whatsapp_number, sms_number: member.sms_number,
        },
        new_value:   form,
        risk_level:  'low',
        flagged:     false,
        timestamp:   now,
        metadata: { member_id: member.id },
      });

      onSaved(updated);
      onClose();
    } catch (err) {
      // Surface the real constraint reason (duplicate whatsapp/sms/email/name)
      // instead of a generic message; fall back only for non-constraint errors.
      setError(memberConstraintMessage(err) || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  return (
    <SubModal title={`Edit — ${member.name}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Full Name *</label>
          <input type="text" value={form.name} onChange={f('name')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
          <input type="email" value={form.email} onChange={f('email')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">WhatsApp Number</label>
          <input type="tel" value={form.whatsapp_number} onChange={f('whatsapp_number')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">SMS Number</label>
          <input type="tel" value={form.sms_number} onChange={f('sms_number')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Notification Preference</label>
          <select value={form.notification_preference} onChange={f('notification_preference')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent">
            <option value="whatsapp_only">WhatsApp only</option>
            <option value="whatsapp_sms">WhatsApp + SMS</option>
            <option value="sms_only">SMS only</option>
            <option value="in_app_only">In-app only</option>
          </select>
        </div>
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex space-x-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Reassign Hands sub-modal ─────────────────────────────────────────────────

const ReassignHandsSubModal = ({ member, groupId, onClose, onSaved }) => {
  const [handsForm, setHandsForm] = useState(
    (member.hands || []).map(h => ({ id: h.id, position_number: String(h.position_number) }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const handleSave = async () => {
    for (const h of handsForm) {
      const pos = parseInt(h.position_number, 10);
      if (isNaN(pos) || pos < 1) { setError('All position numbers must be positive integers'); return; }
    }

    // Check for duplicates within the submitted form itself
    const newPositions = handsForm.map(h => parseInt(h.position_number, 10));
    const uniquePositions = new Set(newPositions);
    if (uniquePositions.size !== newPositions.length) {
      setError('Each hand must have a unique position number.');
      return;
    }

    // Check against existing positions in the group (excluding this member's own hands)
    const ownHandIds = handsForm.map(h => h.id);
    const { data: conflicting } = await supabaseClient
      .from('hands')
      .select('position_number')
      .eq('group_id', groupId)
      .not('id', 'in', `(${ownHandIds.join(',')})`)
      .in('position_number', newPositions);

    if (conflicting && conflicting.length > 0) {
      const conflict = conflicting[0].position_number;
      setError(`Position ${conflict} is already assigned to another member. Please choose a different position.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      for (const hand of handsForm) {
        const { error: handErr } = await supabaseClient
          .from('hands')
          .update({ position_number: parseInt(hand.position_number, 10) })
          .eq('id', hand.id);
        if (handErr) throw handErr;
      }

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'hands_reassigned',
        actor_id:       user?.id ?? null,
        actor_role:     'admin',
        old_value: { positions: member.hands.map(h => h.position_number) },
        new_value: { positions: handsForm.map(h => parseInt(h.position_number, 10)) },
        risk_level:  'medium',
        flagged:     false,
        timestamp:   now,
        metadata: { member_id: member.id, member_name: member.name },
      });

      onSaved();
      onClose();
    } catch (err) {
      setError('Failed to save hand positions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SubModal title={`Reassign Hands — ${member.name}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        {handsForm.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">This member has no hands assigned.</p>
        ) : (
          <>
            <p className="text-xs text-neutral-500">Update the rotation position number for each hand.</p>
            <div className="space-y-3">
              {handsForm.map((hand, i) => (
                <div key={hand.id} className="flex items-center space-x-3">
                  <label className="text-sm text-neutral-700 w-16 flex-shrink-0">Hand {i + 1}</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-neutral-500">Position</span>
                    <input
                      type="number"
                      min="1"
                      value={hand.position_number}
                      onChange={e => {
                        const updated = [...handsForm];
                        updated[i] = { ...updated[i], position_number: e.target.value };
                        setHandsForm(updated);
                      }}
                      className="w-20 px-2 py-1.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex space-x-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || handsForm.length === 0}
            className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Positions'}
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Add Hand sub-modal ───────────────────────────────────────────────────────
// Adds ONE new hand to an existing member at the next-available (or chosen)
// position. It NEVER touches the member's identity (name/phone/email) — it only
// inserts a hands row. This is the supported path to grow a member's hand count;
// "Reassign Hands" only moves existing hands, it cannot add one.

const AddHandSubModal = ({ member, groupId, onClose, onSaved }) => {
  const [position, setPosition]           = useState('');
  const [takenPositions, setTakenPositions] = useState([]);
  const [contributionAmount, setContributionAmount] = useState(0);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    const load = async () => {
      const [{ data: hands }, { data: grp }] = await Promise.all([
        supabaseClient.from('hands').select('position_number').eq('group_id', groupId),
        supabaseClient.from('groups').select('contribution_amount_per_hand').eq('id', groupId).single(),
      ]);
      const taken = (hands || []).map(h => h.position_number).sort((a, b) => a - b);
      setTakenPositions(taken);
      setContributionAmount(grp?.contribution_amount_per_hand || 0);
      // Default to the first unused position.
      const used = new Set(taken);
      let p = 1;
      while (used.has(p)) p += 1;
      setPosition(String(p));
      setLoading(false);
    };
    load();
  }, [groupId]);

  const handleSave = async () => {
    const pos = parseInt(position, 10);
    if (isNaN(pos) || pos < 1) { setError('Position must be a positive integer.'); return; }
    if (takenPositions.includes(pos)) { setError(`Position ${pos} is already taken. Choose another.`); return; }

    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      const { error: insErr } = await supabaseClient
        .from('hands')
        .insert({
          group_id:            groupId,
          member_id:           null,
          member_db_id:        member.id,
          position_number:     pos,
          contribution_amount: contributionAmount,
        });
      if (insErr) throw insErr;

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'hand_added',
        actor_id:       user?.id ?? null,
        actor_role:     'admin',
        new_value:      { position_number: pos },
        risk_level:     'medium',
        flagged:        false,
        timestamp:      now,
        metadata: { member_id: member.id, member_name: member.name, position_number: pos },
      });

      onSaved();
      onClose();
    } catch (err) {
      if (err?.code === '23505') {
        setError(`Position ${pos} was just taken. Please choose another.`);
      } else {
        setError('Failed to add hand. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SubModal title={`Add Hand — ${member.name}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            <p className="text-xs text-neutral-500">
              Adds one new hand for this member. Does not change their name, phone, or email.
            </p>
            <div className="flex items-center space-x-3">
              <label className="text-sm text-neutral-700 w-20 flex-shrink-0">Position</label>
              <input
                type="number"
                min="1"
                value={position}
                onChange={e => setPosition(e.target.value)}
                className="w-24 px-2 py-1.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <p className="text-xs text-neutral-500">
              {takenPositions.length > 0 ? `Taken: ${takenPositions.join(', ')}` : 'No positions assigned yet.'}
            </p>
            <p className="text-xs text-neutral-500">
              Contribution: ${parseFloat(contributionAmount || 0).toFixed(2)} per hand
            </p>
          </>
        )}
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex space-x-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Adding…' : 'Add Hand'}
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Remove Hand sub-modal ────────────────────────────────────────────────────
// Removes ONE hand (the inverse of Add Hand) and recompacts rotation positions
// so there is no gap. The authoritative guards live in the remove_hand RPC
// (atomic delete + recompaction, no-active-cycle, no-financial-history,
// min-hands floor); the pre-checks here just disable rows with a clear reason so
// the admin isn't surprised by a server rejection.
const RemoveHandSubModal = ({ member, group, onClose, onRemoved }) => {
  const groupId = parseInt(group.id, 10);
  const hands = (member.hands || []).slice().sort((a, b) => a.position_number - b.position_number);
  const memberHandCount = hands.length;

  const [loading, setLoading]         = useState(true);
  const [activeCycle, setActiveCycle] = useState(false);
  const [groupHandCount, setGroupHandCount] = useState(0);
  const [footprint, setFootprint]     = useState(new Set()); // hand ids with financial history
  const [working, setWorking]         = useState(null);      // hand id being removed
  const [error, setError]             = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const handIds = hands.map(h => h.id);
      const empty = Promise.resolve({ data: [] });
      const [{ data: grp }, { count: gCount }, pay, po, rnd] = await Promise.all([
        supabaseClient.from('groups').select('current_round_id').eq('id', groupId).single(),
        supabaseClient.from('hands').select('id', { count: 'exact', head: true }).eq('group_id', groupId),
        handIds.length ? supabaseClient.from('payments').select('hand_id').in('hand_id', handIds) : empty,
        handIds.length ? supabaseClient.from('payouts').select('hand_id').in('hand_id', handIds) : empty,
        handIds.length ? supabaseClient.from('rounds').select('collector_hand_id').in('collector_hand_id', handIds) : empty,
      ]);
      if (cancelled) return;
      setActiveCycle(!!grp?.current_round_id);
      setGroupHandCount(gCount || 0);
      const fp = new Set();
      (pay.data || []).forEach(r => fp.add(r.hand_id));
      (po.data || []).forEach(r => fp.add(r.hand_id));
      (rnd.data || []).forEach(r => fp.add(r.collector_hand_id));
      setFootprint(fp);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // hands is derived from the (stable) member prop; groupId keys the fetch.
  }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reasonFor = (hand) => {
    if (activeCycle) return 'A cycle is active — locked';
    if (footprint.has(hand.id)) return 'Has payment history';
    if (memberHandCount <= 1) return "Member's only hand";
    if (groupHandCount <= 2) return 'Group needs ≥ 2 hands';
    return null;
  };

  const handleRemove = async (hand) => {
    setWorking(hand.id);
    setError(null);
    try {
      // Atomic delete + position recompaction, guards enforced server-side.
      const { error: rpcErr } = await supabaseClient.rpc('remove_hand', { p_hand_id: hand.id });
      if (rpcErr) throw new Error(rpcErr.message || 'Could not remove the hand.');

      const { data: { user } } = await supabaseClient.auth.getUser();
      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'hand_removed',
        actor_id:       user?.id ?? null,
        actor_role:     'admin',
        old_value:      { position_number: hand.position_number },
        new_value:      null,
        risk_level:     'medium',
        flagged:        false,
        timestamp:      new Date().toISOString(),
        metadata:       { member_id: member.id, member_name: member.name, hand_id: hand.id },
      });

      onRemoved();   // reload members so recompacted positions show
      onClose();
    } catch (err) {
      setError(err.message || 'Could not remove the hand.');
      setWorking(null);
    }
  };

  return (
    <SubModal title={`Remove Hand — ${member.name}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            {activeCycle && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800">
                Can't remove a hand while a cycle is active — positions and pot are locked until the cycle completes.
              </div>
            )}
            <p className="text-xs text-neutral-500">
              Removes one hand and renumbers the rotation so there's no gap. Only hands
              with no payment history can be removed, and a member must keep at least one hand.
            </p>
            <div className="space-y-2">
              {hands.map(hand => {
                const reason = reasonFor(hand);
                const disabled = !!reason || working !== null;
                return (
                  <div key={hand.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <p className="text-sm text-neutral-900">Position {hand.position_number}</p>
                      {reason && <p className="text-xs text-neutral-500">{reason}</p>}
                    </div>
                    <button onClick={() => handleRemove(hand)} disabled={disabled}
                      className="px-3 py-1.5 text-xs font-medium border border-error-200 rounded-md text-error-600 hover:bg-error-50 disabled:opacity-40 disabled:cursor-not-allowed">
                      {working === hand.id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                );
              })}
            </div>
            {error && <p className="text-sm text-error-600">{error}</p>}
          </>
        )}
        <div className="pt-1">
          <button onClick={onClose}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Close
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Remove confirmation sub-modal ───────────────────────────────────────────

const RemoveConfirmSubModal = ({ member, onClose, onRemoved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleRemove = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      const { error: removeErr } = await supabaseClient
        .from('members')
        .update({ status: 'removed' })
        .eq('id', member.id);
      if (removeErr) throw removeErr;

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'member_removed',
        actor_id:       user?.id ?? null,
        actor_role:     'admin',
        old_value: { status: member.status },
        new_value: { status: 'removed' },
        risk_level:  'high',
        flagged:     false,
        timestamp:   now,
        metadata: { member_id: member.id, member_name: member.name },
      });

      onRemoved(member.id);
      onClose();
    } catch {
      setError('Failed to remove member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SubModal title="Remove Member" onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        <p className="text-neutral-700 text-sm">
          Are you sure you want to remove <strong>{member.name}</strong> from this group?
          This cannot be undone.
        </p>
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800 space-y-1">
          <p>• Member status will be set to <strong>Removed</strong></p>
          <p>• Payment history will be preserved</p>
          <p>• Member will be removed from the active rotation</p>
          <p>• The member record is never deleted</p>
        </div>
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex space-x-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleRemove} disabled={loading}
            className="flex-1 px-4 py-2 bg-error-600 text-white rounded-lg text-sm font-semibold hover:bg-error-700 disabled:opacity-50">
            {loading ? 'Removing…' : 'Remove Member'}
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Payment History sub-modal ────────────────────────────────────────────────

// Renders record rows grouped by cycle (subheading + per-cycle subtotal) so repeated
// round numbers across cycles ("Round 2" in cycle 1 AND cycle 2) never look like
// duplicates. renderRow(p, key) draws one row.
const CycleGroupedList = ({ rows, renderRow }) => (
  <div className="space-y-3">
    {groupByCycle(rows).map((g, gi) => (
      <div key={`cy${g.cycle ?? gi}`}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-bold text-neutral-800">Cycle {g.cycle ?? '—'}</p>
          <p className="text-xs text-neutral-500">subtotal ${g.subtotal.toFixed(2)}</p>
        </div>
        <div className="space-y-2">{g.rows.map((p, i) => renderRow(p, `${gi}-${i}`))}</div>
      </div>
    ))}
  </div>
);

const PaymentHistorySubModal = ({ member, groupId, groupName, onClose }) => {
  // Two-sided record: contributions MADE (own hands) + payments RECEIVED (as this
  // round's collector). Keyed correctly (hand->member_db_id / round->collector),
  // NOT the legacy payments.member_id. Shared with H6 via fetchMemberRecord.
  const [record, setRecord]       = useState({ contributed: [], received: [], organizer: { complete: false }, attestation: null, model: null, member: null });
  const [loading, setLoading]     = useState(true);
  // Organizer attestation (Stage 35 / H5).
  const [attestations, setAttestations] = useState([]);
  // The EXACT statement the organizer will affirm — fetched from the DB
  // (get_current_attestation_statement, Stage 38) so it is byte-for-byte what
  // admin_attest_record hashes (no client-side / name-interpolated divergence).
  const [statementText, setStatementText] = useState('');
  const [affirmed, setAffirmed]   = useState(false);
  const [attesting, setAttesting] = useState(false);
  const [attestError, setAttestError] = useState(null);
  // Certified export (H6).
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const loadAttestations = async () => {
    const { data } = await supabaseClient
      .from('attestations')
      .select('id, attested_at, statement_version')
      .eq('group_id', groupId).eq('member_db_id', member.id)
      .order('attested_at', { ascending: false });
    setAttestations(data || []);
  };

  useEffect(() => {
    const load = async () => {
      setRecord(await fetchMemberRecord({ memberId: member.id, groupId }));
      await loadAttestations();
      const { data: stmt } = await supabaseClient.rpc('get_current_attestation_statement');
      if (stmt) setStatementText(stmt);
      setLoading(false);
    };
    load();
  }, [member, groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAttest = async () => {
    if (!affirmed) return;
    setAttesting(true); setAttestError(null);
    try {
      const { error } = await supabaseClient.rpc('admin_attest_record', {
        p_group_id: groupId,
        p_member_db_id: member.id,
        p_affirm: true,
      });
      if (error) throw error;
      setAffirmed(false);
      await loadAttestations();
      // Refresh the record so the attestation block (and the certified export) enable.
      setRecord(await fetchMemberRecord({ memberId: member.id, groupId }));
    } catch (err) {
      setAttestError(err.message || 'Could not record the attestation.');
    } finally {
      setAttesting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true); setExportError(null);
    try {
      await downloadMemberRecordPdf(record, { groupName });
    } catch (err) {
      setExportError(err.message || 'Could not generate the record PDF.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <SubModal title={`Payment History — ${member.name}`} onClose={onClose}>
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (record.contributed.length === 0 && record.received.length === 0) ? (
          <p className="text-sm text-neutral-500 text-center py-6">No payment records found.</p>
        ) : (
          <div className="space-y-5">
            {/* RECEIVED as collector (payments into this member's pot). */}
            {record.received.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                  Received as collector ({record.received.length})
                </p>
                <CycleGroupedList rows={record.received} renderRow={(p, key) => (
                  <div key={`r${key}`} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-neutral-900">From {p.from || 'Member'}</p>
                      <p className="text-xs text-neutral-500">
                        Round {p.round_number ?? '—'}
                        {p.reference ? ` · Ref: ${p.reference}` : ''}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_STYLES[p.status] || 'bg-neutral-100 text-neutral-600'}`}>
                        {PAYMENT_STATUS_LABELS[p.status] || p.status}
                      </span>
                      <p className="text-xs text-neutral-500">${parseFloat(p.amount ?? 0).toFixed(2)}</p>
                    </div>
                  </div>
                )} />
              </div>
            )}
            {/* CONTRIBUTIONS made (this member's own hands). */}
            {record.contributed.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                  Contributions made ({record.contributed.length})
                </p>
                <CycleGroupedList rows={record.contributed} renderRow={(p, key) => (
                  <div key={`c${key}`} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        Round {p.round_number ?? '—'}{p.paid_to ? ` — Paid to ${p.paid_to}` : ''}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {p.date ? new Date(p.date).toLocaleDateString() : '—'}
                        {p.reference ? ` · Ref: ${p.reference}` : ''}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_STYLES[p.status] || 'bg-neutral-100 text-neutral-600'}`}>
                        {PAYMENT_STATUS_LABELS[p.status] || p.status}
                      </span>
                      <p className="text-xs text-neutral-500">
                        ${parseFloat(p.amount ?? 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )} />
              </div>
            )}
          </div>
        )}

        {/* Organizer attestation (H5). The admin affirms this member's record for an
            FHA/mortgage file; admin_attest_record binds actor+time, hashes the
            statement + record, and writes an immutable, audited attestation. */}
        <div className="mt-6 pt-4 border-t border-neutral-200">
          <h4 className="text-sm font-semibold text-neutral-900 mb-1">Organizer Attestation</h4>
          {attestations.length > 0 && (
            <p className="text-xs text-success-700 mb-2">
              ✓ Attested {new Date(attestations[0].attested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {attestations.length > 1 ? ` (${attestations.length} total)` : ''}
            </p>
          )}
          <p className="text-xs text-neutral-600 mb-3">{statementText}</p>
          <label className="flex items-start gap-2 text-xs text-neutral-700 mb-2">
            <input type="checkbox" checked={affirmed} onChange={e => setAffirmed(e.target.checked)} className="mt-0.5" />
            <span>I affirm the above statement.</span>
          </label>
          {attestError && <p className="text-xs text-error-600 mb-2">{attestError}</p>}
          <button
            type="button"
            onClick={handleAttest}
            disabled={!affirmed || attesting || !statementText}
            className="px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {attesting ? 'Recording…' : 'Attest this record'}
          </button>
        </div>

        {/* Certified export (H6) — requires an existing attestation + complete organizer
            name. The on-screen record above is viewable un-attested; the PDF is not. */}
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <h4 className="text-sm font-semibold text-neutral-900 mb-1">Certified record (PDF)</h4>
          {record.organizer && record.organizer.complete === false ? (
            <p className="text-xs text-warning-700 mb-2">
              Set your full legal name in Group Settings to certify records.
            </p>
          ) : !record.attestation ? (
            <p className="text-xs text-neutral-500 mb-2">
              Attest this record above to enable the certified export.
            </p>
          ) : record.attestation.current === false ? (
            <p className="text-xs text-warning-700 mb-2">
              This record changed since it was certified — re-attest above to export.
            </p>
          ) : (
            <p className="text-xs text-neutral-600 mb-2">
              A self-contained, self-verifying copy of {member.name}'s record for a mortgage file.
            </p>
          )}
          {exportError && <p className="text-xs text-error-600 mb-2">{exportError}</p>}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !record.attestation || record.attestation.current === false || (record.organizer && record.organizer.complete === false)}
            className="px-4 py-2 border border-primary-600 text-primary-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:border-neutral-300 disabled:text-neutral-400"
          >
            {exporting ? 'Preparing…' : 'Export record (PDF)'}
          </button>
        </div>
      </div>
    </SubModal>
  );
};

// ─── Invite link section (per member) ─────────────────────────────────────────
// Flag-gated (invite_link_retrieval). Actions by state:
//   pending  → Copy Invite Link + Share via WhatsApp
//   expired  → Resend (regenerates a FRESH token via createInviteToken — the SAME
//              function used at add-time; the old token stays dead), which then
//              flips the row to pending so Copy/Share act on the NEW link
//   accepted → label only. NO link, NO regenerate — the member logs in with
//              phone+PIN and the link is spent. Re-issuing login access to someone
//              already in is an account-takeover vector; a deliberate "reset member
//              access" flow (confirmation + logging) is a separate, banked feature.
//   none     → Generate Link (defensive; every add creates an invite — never block)
// The WhatsApp text is built with the SAME buildInviteContent() as the post-add
// screen, so the shared message is byte-for-byte identical.
const InviteSection = ({ member, group, adminName, adminId, onRegenerated }) => {
  const [copied, setCopied]     = useState(false);
  const [working, setWorking]   = useState(false);
  const [error, setError]       = useState(null);

  const invite = member.invite;
  const state  = inviteState(invite);
  const label  = INVITE_LABELS[state];

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const whatsappHref = (token) => {
    const positions = (member.hands || []).map(h => h.position_number).sort((a, b) => a - b);
    const firstDueDate = group.start_date
      // Parse date-only at LOCAL midnight (avoid the UTC off-by-one) — matches AddMemberForm.
      ? new Date(`${group.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'TBD';
    const { text } = buildInviteContent({
      groupName:          group.group_name || '',
      adminName,
      contributionAmount: group.contribution_amount_per_hand || 0,
      frequency:          group.contribution_frequency || '',
      positions,
      firstDueDate,
      inviteUrl:          inviteUrl(token),
    });
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — leave label unchanged
    }
  };

  const handleRegenerate = async () => {
    setWorking(true);
    setError(null);
    try {
      const fresh = await createInviteToken({
        groupId:     parseInt(group.id, 10),
        memberDbId:  member.id,
        adminId,
      });
      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type:     'invite_regenerated',
        actor_id:       adminId ?? null,
        actor_role:     'admin',
        old_value:      { previous_status: invite?.status ?? null },
        new_value:      { invite_id: fresh.id, expires_at: fresh.expires_at },
        risk_level:     'medium',
        flagged:        false,
        timestamp:      new Date().toISOString(),
        metadata:       { member_id: member.id, member_name: member.name },
      });
      onRegenerated(member.id, fresh);
    } catch {
      setError('Failed to generate a new link. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const linkActions = (token) => (
    <>
      <button onClick={() => handleCopy(token)}
        className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50">
        {copied ? '✓ Copied!' : 'Copy Invite Link'}
      </button>
      <a href={whatsappHref(token)} target="_blank" rel="noopener noreferrer"
        className="px-3 py-1.5 text-xs font-medium border border-success-300 rounded-md text-success-700 hover:bg-success-50">
        Share via WhatsApp
      </a>
    </>
  );

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-neutral-100">
      <span className="text-neutral-400 text-xs">Invite</span>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${label.cls}`}>{label.text}</span>

      {state === 'pending' && linkActions(invite.token)}

      {(state === 'expired' || state === 'none') && (
        <button onClick={handleRegenerate} disabled={working}
          className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
          {working
            ? (state === 'none' ? 'Generating…' : 'Resending…')
            : (state === 'none' ? 'Generate Link' : 'Resend')}
        </button>
      )}

      {/* accepted → label only: no link, no regenerate (account-takeover guard). */}

      {error && <span className="text-xs text-error-600 w-full">{error}</span>}
    </div>
  );
};

// ─── Member row ───────────────────────────────────────────────────────────────

const MemberRow = ({ member, group, adminName, adminId, invitesEnabled, susuEnabled, activeCycle, onRegenerated, onEdit, onReassign, onAddHand, onRemoveHand, onRemove, onViewPayments }) => {
  const positions = (member.hands || [])
    .map(h => h.position_number)
    .sort((a, b) => a - b);

  return (
    <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
      {/* Top row: avatar + name + status */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
            <span className="text-neutral-50 text-sm font-semibold">
              {member.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-neutral-900">
              {member.name}
              {member.display_alias && member.display_alias.trim() && (
                <span className="ml-1 font-normal text-neutral-500">(shows as: {member.display_alias.trim()})</span>
              )}
            </p>
            <p className="text-xs text-neutral-500">
              Joined {member.joined_date
                ? new Date(member.joined_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'unknown'}
            </p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[member.status] || STATUS_STYLES.inactive}`}>
          {STATUS_LABELS[member.status] || member.status}
        </span>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <span className="text-neutral-400">Email</span>
          <p className="text-neutral-700 truncate">{member.email || '—'}</p>
        </div>
        <div>
          <span className="text-neutral-400">WhatsApp</span>
          <p className="text-neutral-700">{member.whatsapp_number}</p>
        </div>
        <div>
          <span className="text-neutral-400">Hands</span>
          <p className="text-neutral-700">
            {positions.length
              ? `${positions.length} hand${positions.length > 1 ? 's' : ''} — pos. ${positions.join(', ')}`
              : 'None assigned'}
          </p>
        </div>
        <div>
          <span className="text-neutral-400">Notifications</span>
          <p className="text-neutral-700">{NOTIFICATION_LABELS[member.notification_preference] || member.notification_preference}</p>
        </div>
      </div>

      {/* Agreement acknowledgment (additive read of member_consents). Shows the
          documents present on the row; old rows lack privacy/tc and just show the
          Agreement. */}
      <div className="text-xs">
        <span className="text-neutral-400">Agreement</span>
        {member.consent ? (
          <p className="text-success-700">
            Agreed ✓ · {new Date(member.consent.agreed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' · '}Agreement {member.consent.terms_version}
            {member.consent.privacy_accepted && member.consent.privacy_version ? ` · Privacy ${member.consent.privacy_version}` : ''}
            {member.consent.tc_accepted && member.consent.tc_version ? ` · Terms ${member.consent.tc_version}` : ''}
          </p>
        ) : (
          <p className="text-warning-700">Not yet acknowledged</p>
        )}
      </div>

      {/* Invite status + link actions (flag-gated) */}
      {invitesEnabled && (
        <InviteSection
          member={member}
          group={group}
          adminName={adminName}
          adminId={adminId}
          onRegenerated={onRegenerated}
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={() => onEdit(member)}
          className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50">
          Edit
        </button>
        <button onClick={() => onReassign(member)}
          disabled={!positions.length}
          className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed">
          Reassign Hands
        </button>
        {/* Roster is fixed at cycle start: adding a hand mid-cycle injects an
            un-back-filled active hand (no rotation slot, not in contributor_count/
            pot, yet owed on future rounds). Disable while a cycle is running; add
            members at the next cycle's roster step. Enabled between cycles. */}
        <button onClick={() => onAddHand(member)}
          disabled={susuEnabled && activeCycle}
          title={susuEnabled && activeCycle ? 'Add members at the start of the next cycle.' : undefined}
          className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed">
          Add Hand
        </button>
        {susuEnabled && (
          <button onClick={() => onRemoveHand(member)}
            disabled={!positions.length}
            className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed">
            Remove Hand
          </button>
        )}
        <button onClick={() => onViewPayments(member)}
          className="px-3 py-1.5 text-xs font-medium border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50">
          Payment History
        </button>
        <button onClick={() => onRemove(member)}
          className="px-3 py-1.5 text-xs font-medium border border-error-200 rounded-md text-error-600 hover:bg-error-50">
          Remove
        </button>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ManageMembersModal = ({ group, onClose }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Invite-link retrieval (flag-gated). adminName/adminId feed the WhatsApp
  // message + the regenerate audit log; both are resolved once on mount.
  const [invitesEnabled, setInvitesEnabled] = useState(false);
  // Remove Hand is gated behind susu_rounds (the cycle-engine flag).
  const [susuEnabled, setSusuEnabled] = useState(false);
  // Live-round signal: true while a cycle is running (groups.current_round_id set).
  // Add Hand is disabled mid-cycle — the roster is fixed at cycle start.
  const [activeCycle, setActiveCycle] = useState(false);
  const [adminName, setAdminName] = useState('Your Group Admin');
  const [adminId, setAdminId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: { user } }, enabled, susu, { data: grp }] = await Promise.all([
        supabaseClient.auth.getUser(),
        checkFeatureFlag(FEATURE_FLAGS.INVITE_LINK_RETRIEVAL),
        checkFeatureFlag(FEATURE_FLAGS.SUSU_ROUNDS),
        supabaseClient.from('groups').select('current_round_id').eq('id', parseInt(group.id, 10)).single(),
      ]);
      if (cancelled) return;
      setInvitesEnabled(enabled);
      setSusuEnabled(susu);
      setActiveCycle(!!grp?.current_round_id);
      if (user) {
        setAdminId(user.id);
        setAdminName(
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          user.email?.split('@')[0] ||
          'Your Group Admin'
        );
      }
    })();
    return () => { cancelled = true; };
  }, [group.id]);

  // Sub-modal state
  const [editMember,    setEditMember]    = useState(null);
  const [reassignMember, setReassignMember] = useState(null);
  const [addHandMember, setAddHandMember] = useState(null);
  const [removeHandMember, setRemoveHandMember] = useState(null);
  const [removeMember,  setRemoveMember]  = useState(null);
  const [paymentsMember, setPaymentsMember] = useState(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    // Parse to integer — members.group_id is a bigint FK to groups.id
    const numericGroupId = parseInt(group.id, 10);

    // Step 1: fetch members (no embedded join — avoids PostgREST ambiguous-FK error
    // caused by hands having both member_db_id→members.id and member_id→auth.users)
    // Active members only — removed members are preserved in the DB for audit but never shown here
    const { data: membersData, error: membersErr } = await supabaseClient
      .from('members')
      .select('*')
      .eq('group_id', numericGroupId)
      .eq('status', 'active')
      .order('joined_date', { ascending: true });

    if (membersErr) {
      console.error('ManageMembersModal — members query failed:', membersErr);
      setLoadError(`Failed to load members: ${membersErr.message}`);
      setLoading(false);
      return;
    }

    if (!membersData || membersData.length === 0) {
      setMembers([]);
      setLoading(false);
      return;
    }

    // Step 2: fetch hands separately, joined by member_db_id (bigint FK to members.id)
    const memberIds = membersData.map(m => m.id);
    const { data: handsData, error: handsErr } = await supabaseClient
      .from('hands')
      .select('id, position_number, contribution_amount, member_db_id')
      .in('member_db_id', memberIds);

    if (handsErr) {
      console.error('ManageMembersModal — hands query failed:', handsErr);
      // Non-fatal: show members without hands rather than blocking the whole view
    }

    // Step 3: fetch invites, joined by member_db_id. Ordered newest-first so the
    // first match per member is the LATEST invite (createInviteToken always INSERTs
    // a new row — regenerating leaves older rows behind, so we must take the newest).
    // Read regardless of the flag (one small keyed query); the UI is what's gated.
    const { data: invitesData, error: invitesErr } = await supabaseClient
      .from('member_invites')
      .select('id, token, status, created_at, expires_at, member_db_id')
      .in('member_db_id', memberIds)
      .order('created_at', { ascending: false });

    if (invitesErr) {
      console.error('ManageMembersModal — invites query failed:', invitesErr);
      // Non-fatal: fall through with no invite data rather than blocking the view
    }

    // Latest acknowledgment (consent) per member — additive read; RLS lets the
    // admin read its group's consents. Newest-first, take the first per member.
    const { data: consentData, error: consentErr } = await supabaseClient
      .from('member_consents')
      .select('member_db_id, agreed_at, terms_version, signature, privacy_accepted, privacy_version, tc_accepted, tc_version')
      .in('member_db_id', memberIds)
      .order('agreed_at', { ascending: false });
    if (consentErr) {
      console.error('ManageMembersModal — consents query failed:', consentErr);
    }

    // Attach hands + latest invite + latest consent to their member
    const membersWithHands = membersData.map(m => ({
      ...m,
      hands: handsData ? handsData.filter(h => h.member_db_id === m.id) : [],
      invite: invitesData ? (invitesData.find(i => i.member_db_id === m.id) || null) : null,
      consent: consentData ? (consentData.find(c => c.member_db_id === m.id) || null) : null,
    }));

    setMembers(membersWithHands);
    setLoading(false);
  }, [group.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleMemberUpdated = (updated) => {
    setMembers(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
  };

  const handleMemberRemoved = (memberId) => {
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const handleHandsReassigned = () => { loadMembers(); };

  // Swap in the freshly-minted invite so the row re-derives to 'pending' and
  // exposes Copy/Share of the NEW link (the old token is already dead server-side).
  const handleInviteRegenerated = (memberId, freshInvite) => {
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, invite: freshInvite } : m));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-neutral-100 rounded-lg w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Manage Members</h2>
              <p className="text-xs text-neutral-500 mt-0.5">{group.group_name}</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => { onClose(); navigate(`/admin/group/${group.id}/add-member`); }}
                className="flex items-center space-x-1.5 px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Member</span>
              </button>
              <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
            </div>
          </div>

          {/* Member list */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
              </div>
            ) : loadError ? (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4 mt-4 text-sm text-error-700">
                <p className="font-medium mb-1">Could not load members</p>
                <p className="text-xs text-error-600">{loadError}</p>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-500">No active members yet.</p>
                <button
                  onClick={() => { onClose(); navigate(`/admin/group/${group.id}/add-member`); }}
                  className="mt-4 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700"
                >
                  Add First Member
                </button>
              </div>
            ) : (
              <div className="space-y-3 mt-2">
                {members.map(member => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    group={group}
                    adminName={adminName}
                    adminId={adminId}
                    invitesEnabled={invitesEnabled}
                    susuEnabled={susuEnabled}
                    activeCycle={activeCycle}
                    onRegenerated={handleInviteRegenerated}
                    onEdit={setEditMember}
                    onReassign={setReassignMember}
                    onAddHand={setAddHandMember}
                    onRemoveHand={setRemoveHandMember}
                    onRemove={setRemoveMember}
                    onViewPayments={setPaymentsMember}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {editMember && (
        <EditMemberSubModal
          member={editMember}
          onClose={() => setEditMember(null)}
          onSaved={handleMemberUpdated}
        />
      )}
      {reassignMember && (
        <ReassignHandsSubModal
          member={reassignMember}
          groupId={parseInt(group.id, 10)}
          onClose={() => setReassignMember(null)}
          onSaved={handleHandsReassigned}
        />
      )}
      {addHandMember && (
        <AddHandSubModal
          member={addHandMember}
          groupId={parseInt(group.id, 10)}
          onClose={() => setAddHandMember(null)}
          onSaved={handleHandsReassigned}
        />
      )}
      {removeHandMember && (
        <RemoveHandSubModal
          member={removeHandMember}
          group={group}
          onClose={() => setRemoveHandMember(null)}
          onRemoved={handleHandsReassigned}
        />
      )}
      {removeMember && (
        <RemoveConfirmSubModal
          member={removeMember}
          onClose={() => setRemoveMember(null)}
          onRemoved={handleMemberRemoved}
        />
      )}
      {paymentsMember && (
        <PaymentHistorySubModal
          member={paymentsMember}
          groupId={parseInt(group.id, 10)}
          groupName={group.group_name || group.name}
          onClose={() => setPaymentsMember(null)}
        />
      )}
    </>
  );
};

export default ManageMembersModal;
